import style from './historie-element.module.css'

type State = 'INITIAL' | 'LEFT' | 'RIGHT'
export class HistorieElement extends HTMLElement {
  #isWatching = false
  #observer

  #state: State = 'INITIAL'

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
      this.#setupIntersectionObserver()
      this.#updateClasses()
      this.querySelector('button')?.addEventListener('click', () => {
        this.#scrollToOther()
      })
    } else {
      // Skip directly to state with no animation
      this.#state = this.side === 'left' ? 'LEFT' : 'RIGHT'
    }
  }

  disconnectedCallback() {
    this.#disconnectIntersectionObserver()
  }

  /** Intersection Observer */

  #setupIntersectionObserver() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#startWatching()
          } else {
            this.#stopWatching()
          }
        }
      },
      {
        threshold: 0,
      },
    )
    this.#observer.observe(this)
  }

  #disconnectIntersectionObserver() {
    this.#observer.disconnect()
  }

  /** Scroll watcher */

  #startWatching() {
    if (this.#isWatching) {
      return
    }
    this.#isWatching = true

    const loop = () => {
      this.#checkTriggers()
      if (this.#isWatching) {
        requestAnimationFrame(loop)
      }
    }
    requestAnimationFrame(loop)
  }

  async #checkTriggers() {
    const bcr = this.getBoundingClientRect()
    const boundaryCrossed = bcr.top < 0.5 * window.innerHeight

    let nextState: State
    if (boundaryCrossed) {
      nextState = this.side === 'left' ? 'LEFT' : 'RIGHT'
    } else {
      nextState = 'INITIAL'
    }

    if (this.#state !== nextState) {
      this.#state = nextState
      this.#updateClasses()
    }
  }

  #updateClasses() {
    this.classList.remove(style.initial, style.leftActive, style.rightActive)
    switch (this.#state) {
      case 'INITIAL':
        this.classList.add(style.initial)
        break
      case 'LEFT':
        this.classList.add(style.leftActive)
        break
      case 'RIGHT':
        this.classList.add(style.rightActive)
        break
    }
  }

  #stopWatching() {
    this.#isWatching = false
  }

  #scrollToOther() {
    const currentElement = this
    const all = Array.from(document.querySelectorAll(`.${style.historieElement}`))
    const nextElement = all.find(
      (historieElement) => historieElement !== currentElement,
    ) as HistorieElement

    // Toggle state of next element to the current element to create a smooth transition.
    // It will transition to its own state automatically
    nextElement.classList.toggle(style.skipTransitions, true)
    nextElement.#state = this.#state
    nextElement.#updateClasses()
    // eslint-disable-next-line no-unused-expressions
    nextElement.offsetTop
    nextElement.classList.toggle(style.skipTransitions, false)

    // Scroll so that the next element matches the current element
    const currentTop = currentElement.getBoundingClientRect().top
    const otherTop = nextElement.getBoundingClientRect().top
    document.scrollingElement.scrollTop += otherTop - currentTop

    // Set focus to the first h2 after the next element
    const nextSection = nextElement.closest('.dh-sa-section')
    const nextTitle = nextSection.querySelector('h2,h3') as HTMLElement
    nextTitle.setAttribute('tabindex', '-1')
    nextTitle.focus()
  }

  get side(): 'left' | 'right' {
    let value = this.getAttribute('side')
    switch (value) {
      case 'left':
      case 'right':
        return value
      default:
        throw new Error('unknown side attribute')
    }
  }
}
